import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DialPlanGroupContext } from '../../../../helpdesk/shared/contexts/dial-plan-group.context';
import { DialPlanManagementGroup, DnDidRange, SiteSummary } from '../../../../shared/models/generated/ziroModels';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { SiteSummaryContext } from '../../../../shared/contexts/site-summary.context';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../shared/services/toast.service';
import { Observable, Subscriber, Subscription } from 'rxjs';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../../shared/bottom-nav/bottom-nav.service';
import { RangeFilterService } from '../../../services/range-filter.service';
import { TelephoneNumberFilter } from '../../../../shared/filters/telephone-number.filter';
import { DatatableBadge, DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';

interface CiscoDialPlanManagementRow extends DatatableRow {
  id: string;
  name: string;
  sites: string[];
  dnRanges: DnDidRange[];
  didRanges?: DnDidRange[];
}

@Component({
  selector: 'ziro-cisco-dial-plan-management-list',
  templateUrl: './dial-plan-management-list.component.html',
  styleUrls: ['../../../admin-page.scss', './dial-plan-management-list.component.scss'],
  providers: [RangeFilterService],
  standalone: false,
})
export class CiscoDialPlanManagementListComponent implements OnInit, OnDestroy {
  private _dialPlanGroupContext = inject(DialPlanGroupContext);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _siteSummaryContext = inject(SiteSummaryContext);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _toastService = inject(ToastService);
  private _bottomNavService = inject(BottomNavService);
  private _rangeFilterService = inject(RangeFilterService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);

  isLoading = true;
  tableDataAutomation = 'dial-plan-management-list-table';

  columns: DatatableColumn<CiscoDialPlanManagementRow>[] = [
    {
      name: 'id',
      label: 'tkey;global.table.id.text',
      fixedColumnSizeStyleAttribute: '8%',
    },
    {
      name: 'name',
      label: 'tkey;dialplanmanagement.admin.group.name',
      fixedColumnSizeStyleAttribute: '8%',
    },
    {
      name: 'sites',
      label: 'tkey;dialplanmanagement.admin.group.sites',
      fixedColumnSizeStyleAttribute: '16%',
      sortFn: (a: string[], b: string[]) => a[0]?.localeCompare(b[0]),
    },
    {
      name: 'dnRanges',
      label: 'tkey;dialplanmanagement.admin.group.dnranges',
      fixedColumnSizeStyleAttribute: '28%',
      sortFn: (a: DnDidRange[], b: DnDidRange[]) => {
        const aRange: DnDidRange = a[0] || { start: '0', end: '' };
        const bRange: DnDidRange = b[0] || { start: '0', end: '' };
        return parseInt(aRange.start) - parseInt(bRange.start);
      },
    },
  ];
  data: CiscoDialPlanManagementRow[] = [];

  private _subscription = new Subscription();

  ngOnInit(): void {
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;dialplanmanagement.title' }]);
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'bottom-nav-add-group-button',
          label: 'tkey;dialplanmanagement.admin.add_group',
          icon: ZiroIcons.ADD,
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'bottom-nav-add-group-button',
          type: ButtonTypes.LINK,
          href: window.location.href + '/dial-plan-group',
        },
      ]),
    );

    const siteSummarySub = this._siteSummaryContext.state$.subscribe((state: SiteSummary) => {
      this._initDialPlanGroups(state);
    });
    this._subscription.add(siteSummarySub);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private _onDeleteClicked(group: DialPlanManagementGroup): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        promptBody: this._translateService.instant('tkey;global.sure.delete.dialplanmanagement.group', {
          name: group.name,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'prompt-modal-cancel',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'prompt-modal-confirm',
            cb: () => this._deleteGroup(group),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _getE164FormattedRange(range: { start: string; end: string }): string {
    return this._rangeFilterService.getE164FormattedRange(range);
  }

  private _deleteGroup(group: DialPlanManagementGroup): Observable<void> {
    return new Observable((subscriber: Subscriber<void>) => {
      this._dialPlanGroupContext.delete(group.id).subscribe({
        next: () => {
          this._dialPlanGroupContext.init().subscribe(() => {
            this._toastService.pushDeleteToast('tkey;dialplanmanagement.save.successful.toast', group.name);
          });
          subscriber.next(null);
          subscriber.complete();
        },
        error: (err) => {
          subscriber.next();
          subscriber.complete();
          throw err;
        },
      });
    });
  }

  private _initDialPlanGroups(siteSummary: SiteSummary): void {
    const dialPlanContextSub = this._dialPlanGroupContext.state$.subscribe(
      (dialPlanGroups: DialPlanManagementGroup[]) => {
        const translationPatternColExists = this.columns.find((col) => col.name === 'didRanges');
        if (
          dialPlanGroups.some((group) => group.translationPatternRangesSection.translationPatternRanges.length) &&
          !translationPatternColExists
        ) {
          // add the translation pattern column, if it doesn't exist.
          this.columns.push({
            name: 'didRanges',
            label: 'tkey;dialplanmanagement.admin.group.didranges',
            sortFn: (a: DnDidRange[], b: DnDidRange[]) => {
              const aRange: DnDidRange = a[0] || { start: '0', end: '' };
              const bRange: DnDidRange = b[0] || { start: '0', end: '' };
              return parseInt(aRange.start) - parseInt(bRange.start);
            },
          });
        } else if (translationPatternColExists) {
          // delete the translation pattern column, if it exists.
          this.columns.pop();
        }

        this.data = dialPlanGroups
          .sort((a, b) => a.name.toString().localeCompare(b.name.toString()))
          .map((group: DialPlanManagementGroup): CiscoDialPlanManagementRow => {
            const sites = group.clusterId
              ? [siteSummary.clusters.find((cluster) => cluster.id === group.clusterId).name]
              : group.siteIds.map((siteId) => {
                  for (const cluster of siteSummary.clusters) {
                    for (const site of cluster.sites) {
                      if (site.id === siteId) {
                        return site.name;
                      }
                    }
                  }
                });
            return {
              id: group.id,
              name: group.name,
              sites,
              dnRanges: group.directoryNumberRangesSection.dnRanges.sort(
                (a, b) => parseInt(a.start) - parseInt(b.start),
              ),
              didRanges: group.translationPatternRangesSection.translationPatternRanges.sort(
                (a, b) => parseInt(a.start) - parseInt(b.start),
              ),
              badges: {
                sites: {
                  maxLength: 30,
                  values: sites.map((site) => ({
                    label: site,
                    cssClass: 'bg-secondary',
                    showTooltipOnTruncate: true,
                  })),
                },
                dnRanges: {
                  values: group.directoryNumberRangesSection.dnRanges.map(
                    (range: DnDidRange): DatatableBadge<CiscoDialPlanManagementRow> =>
                      range.start === range.end
                        ? { label: this._telephoneNumberFilter.transform(range.start), cssClass: 'bg-secondary' }
                        : { label: this._rangeFilterService.getE164FormattedRange(range), cssClass: 'bg-secondary' },
                  ),
                },
                didRanges: {
                  values: group.translationPatternRangesSection.translationPatternRanges.map(
                    (range: DnDidRange): DatatableBadge<CiscoDialPlanManagementRow> =>
                      range.start === range.end
                        ? { label: this._telephoneNumberFilter.transform(range.start), cssClass: 'bg-secondary' }
                        : { label: this._rangeFilterService.getE164FormattedRange(range), cssClass: 'bg-secondary' },
                  ),
                },
              },
              actions: [
                {
                  dataAutomation: 'cisco-dial-plan-group-edit',
                  style: ButtonStyles.OUTLINE_PRIMARY,
                  type: ButtonTypes.LINK,
                  icon: ZiroIcons.EDIT,
                  href: window.location.href + `/dial-plan-group/${group.id}`,
                },
                {
                  dataAutomation: 'cisco-dial-plan-group-delete',
                  style: ButtonStyles.OUTLINE_DANGER,
                  type: ButtonTypes.BUTTON,
                  icon: ZiroIcons.DELETE_SMALL,
                  onClick: () => this._onDeleteClicked(group),
                },
                {
                  dataAutomation: 'cisco-dial-plan-group-copy',
                  style: ButtonStyles.OUTLINE_DEFAULT,
                  type: ButtonTypes.LINK,
                  icon: ZiroIcons.COPY,
                  href: window.location.href + `/dial-plan-group/${group.id}/copy`,
                },
              ],
            };
          });

        this.isLoading = false;
      },
    );
    this._subscription.add(dialPlanContextSub);
  }
}
